import React from "react";
import SupernovaCard from "./SupernovaCard";
import { METRIC } from "./supernovaSummary";
import { fmtCount } from "./supernovaFormat";

/**
 * "Your collection" — one card per token type in the collection, always all
 * four, so a member who holds none of a type still sees that it exists.
 *
 * Counts are per-type and come from the chain reads. The staked count is its
 * own read and can be missing on its own: when it is, the card says "stake
 * count unavailable" rather than "0 staked", which would be a claim we can't
 * support — and it draws no staked pill, for the same reason.
 *
 * The Stake button opens the stake modal (SupernovaStakeModal), which previews
 * the lock and then asks the member's own wallet to sign it. It is never wired
 * to anything that signs here — this app has no key and no write path of its
 * own (supernovaChain.js). A card with nothing in the wallet stays disabled: a
 * staked NFT is held by the staking contract and can't be staked twice.
 */

const STAKE_MODAL_MISSING = "The stake flow isn't available on this screen.";
const NOTHING_TO_STAKE =
  "None of this type is in your wallet to stake. Staked NFTs are already held by the staking contract.";
const STAKE_COUNT_UNAVAILABLE =
  "Stake data isn't connected yet, so we can't show how many of this type you have staked.";

const StakeButton = ({ row, onStake }) => {
  const modalReady = typeof onStake === "function";
  const disabled = !modalReady || !row.stakeable;
  const reason = !modalReady
    ? STAKE_MODAL_MISSING
    : !row.stakeable
    ? NOTHING_TO_STAKE
    : undefined;

  return (
    <button
      type="button"
      className="sn-ghost"
      disabled={disabled}
      title={reason}
      onClick={modalReady ? () => onStake(row.id) : undefined}
    >
      Stake
      {reason && <span className="sr-only"> — {reason}</span>}
    </button>
  );
};

/**
 * The card's counts, in the mockup's footer shape: each fact is one element, so
 * it reads as "43 in wallet · 2 staked" rather than as one long sentence.
 */
const CountLine = ({ row }) => {
  const stakedUnknown = row.staked === null;
  return (
    <p className="sn-own-counts tnum" title={`Token #${row.id}`}>
      <span>
        <b>{fmtCount(row.inWallet)}</b> in wallet
      </span>
      {stakedUnknown ? (
        <span className="text-arc-ink-4" title={STAKE_COUNT_UNAVAILABLE}>
          stake count unavailable
        </span>
      ) : (
        <span>
          <b>{fmtCount(row.staked)}</b> staked
        </span>
      )}
      {!stakedUnknown && row.owned !== null && (
        <span className="text-arc-ink-4">{fmtCount(row.owned)} total</span>
      )}
    </p>
  );
};

const SupernovaCollectionList = ({ summary, onStake }) => (
  <>
    <div className="sn-grid" data-testid="supernova-collection-list">
      {summary.tokens.map((row) => (
        <SupernovaCard
          key={row.id}
          tokenId={row.id}
          name={row.name}
          /* No pill for an unread count, and none for a known zero: the pill is
             a claim that something of this type is staked. */
          pill={row.staked ? `${fmtCount(row.staked)} staked` : null}
          footer={<CountLine row={row} />}
          action={<StakeButton row={row} onStake={onStake} />}
        />
      ))}
    </div>
    {summary.staked.status === METRIC.UNAVAILABLE && (
      <p className="text-caption text-arc-ink-4 mt-3.5">
        {summary.staked.note || STAKE_COUNT_UNAVAILABLE}
      </p>
    )}
  </>
);

export default SupernovaCollectionList;
